import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from './PageWrapper';
import Page from './Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import './GroupsHome.scss';
import { Alert, Anchor, Dropdown, DropdownItem, Popup, Tooltip } from '@spark-labs/avocado-ui';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleButton,
  BubbleCheckbox,
  BubbleInput,
  BubbleDebounce,
  BubbleOverlay,
  BubbleTable,
  BubbleDrawer,
  BubbleAccordion,
  BubbleFormGroup,
  BubbleBadge,
  BubbleTypeahead,
  BubbleNestedDropdown,
} from '@go-client/components/BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import {
  bare,
  bareVenueRequest,
  exportGroupBookings,
  exportGroupBookingsBrand,
  exportGroupMemo,
  exportGroupReportCSV,
  exportLetter,
  update as updateGroupBooking,
} from '@go-client/lib/api/groupBooking';
import {
  groupBy,
  GroupedItems,
  intersection,
  mapBy,
  notUndefined,
  onlyUnique,
} from '@go-shared/lib/functional';
import {
  GroupBooking,
  GroupSubgroup,
  VenueRequest,
  VenueRequestStatus,
  venueRequestStatuses,
  BareSimpleGroupBooking,
  ResourceLegacy,
} from '@go-shared/types/groupBooking';
import { hashStringToColor, makeFullName } from '@go-shared/lib/utils';
import {
  timeToString,
  dateToIsoUTC,
  addDaysToDateUTC,
  calculateTimeOffset,
  getLocaleDateStringUTC,
  getLocaleDateTimeStringUTC,
  timeCompare,
  DEFAULT_TIMESTR_FORMAT,
  addDaysToIsoDate,
  timeToMinutes,
} from '@go-shared/lib/time';
import GroupBookingForm from '@go-client/components/GroupBookingForm/GroupBookingForm';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import GroupsGlance from '@go-client/components/GroupsGlance';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import Fuse from 'fuse.js';
import { findSailingBundleByDateAndDay, getDayLocation } from '@go-shared/lib/sailing';
import {
  formatOtherResources,
  OtherResourceGroup,
  venueRequestMeetsBookingHours,
} from '@go-shared/lib/groupBooking';
import { ObjectId } from 'bson';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  bulkBookingStatusChange,
  fetchDataBundle,
  fetchMultiDataBundle,
  GroupBookingDataBundle,
} from '@go-client/components/GroupBookingForm/Workflows';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import uuid from 'uuid';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { camelToSentenceCase, randomString } from '@go-shared/lib/string';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import {
  canApproveOrRejectABooking,
  canBulkSelectBookings,
  canChangeBookingStatus,
  canCreateOrEditBookings,
  canViewGroupsMultiPropertyCheckbox,
  canExportGroupLetters,
} from '@go-shared/lib/permissions';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { Property } from '@go-shared/types/property';
import { DEFAULT_DAY_START, getHourFormat } from '@go-shared/lib/property';
import { DayMarkersButton } from '@go-client/components/DayMarkersButton';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import { groupBookingClient } from '@go-client/lib/api/v2/groupBooking';
import { safeFetch } from '@go-client/lib/api';
import { getUserToken } from '@go-client/lib/auth';
import { NestedDropdownItem } from '@go-client/components/BubbleUI/BubbleNestedDropdown';
import { downloadBlob } from '@go-client/lib/utils';
import { AllResourceTypes, GroupResourceType } from '@go-shared/types/zod/groupResource';
import { getStorage, setStorage } from '@go-client/lib/storage';
import { User } from '@go-shared/types/user';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';
export interface DefaultMode {
  type: 'default';
}

interface GroupEditMode {
  type: 'group-edit';
  groupId?: string;
}

interface SubgroupEditMode {
  type: 'subgroup-edit';
  groupId: string;
  subgroupId: string;
}

export interface BookingEditMode {
  type: 'edit';
  groupId: string;
  bookingId: string;
}

export interface BookingCreateMode {
  type: 'create';
  groupId?: string;
  bookingId?: string;
  startAtPage: number;
}

interface ExportOptions {
  approvedOnly: boolean;
  fromDate?: string;
  toDate?: string;
}

interface GroupsTableItem {
  id: string;
  name: string;
  property: string;
  venue: string;
  voyage: string;
  location: string;
  day: number;
  date: string;
  time: string;
  requester: string;
  createdAt: string;
  status: VenueRequestStatus;
  foodBeverage: string[];
  entertainment: string[];
  housekeeping: string[];
  otherResources: OtherResourceGroup[];
  legacyResources: ResourceLegacy[];

  color: string;
  isFirstReq: boolean;
  isLastReq: boolean;
  isExpandedReq: boolean;
  totalNumRows: number;
  amtSkippedReqs: number;
  hasExpandedReq: boolean;

  checked: boolean;

  _group: GroupBooking;
  _booking: VenueRequest;
  _sailingDay?: BareSimpleSailingDay;
}

type GroupsTableFields = BubbleTableFields<GroupsTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

interface GroupsFilters {
  startDate?: string;
  endDate?: string;
  selectedVoyages: Array<BubbleTypeaheadValue<string>>;
  selectedLocations: Array<BubbleTypeaheadValue<string>>;
  selectedGroupResourceType: string;
  selectedStatuses: Array<BubbleTypeaheadValue<string>>;
  selectedVenues: Array<BubbleTypeaheadValue<VenueTemplate>>;
  selectedProperties: Array<BubbleTypeaheadValue<Property>>;
  selectedRequesters: Array<BubbleTypeaheadValue<User>>;
  groupId: string;
  showAllOfGroupId: string;
  groupByGroup: boolean;
  multiPropertyMode: boolean;
  visibleFields: { [key: string]: boolean };
  showArchivedGroups: boolean;
}

export interface GroupHistoryMode {
  type: 'group-history';
  group: BareSimpleGroupBooking;
}

export type GroupsMode =
  | DefaultMode
  | SubgroupEditMode
  | GroupEditMode
  | BookingEditMode
  | BookingCreateMode
  | GroupHistoryMode;

interface GroupsHomeParams {
  property: string;
}

interface CreateSubgroupForm {
  letter: string;
  number: string;
  _advanced: boolean;
}

function generateDefaultSubgroupForm(subgroups: GroupSubgroup[]): CreateSubgroupForm {
  let letr = '';
  let numb = '';

  if (subgroups) {
    const usedOpts = subgroups.map(sg => sg.label);

    const allOpts = Array.from(Array(26))
      .map((_, i) => i + 65)
      .map(l => Array.from(Array(10).keys()).map(n => `${String.fromCharCode(l)}${n}`))
      .flat();

    const firstAvailOpt = allOpts.find(opt => !usedOpts.includes(opt)) || allOpts[0];

    letr = firstAvailOpt[0];
    numb = firstAvailOpt[1];
  }

  return {
    letter: letr,
    number: numb,
    _advanced: false,
  };
}

type GroupsHomeDataBundle = GroupBookingDataBundle & {
  sailingDayMap: GroupedItems<{
    dateIso: string;
    dayIndex: number;
    sailing: BareSimpleSailing;
    day: BareSimpleSailingDay;
  }>;
};

type GroupsHomePageProps = RouteComponentProps<GroupsHomeParams>;

const GroupsHome: React.FC<GroupsHomePageProps> = (props: GroupsHomePageProps) => {
  const serverInfo = useServerInfo();
  const { serverDate } = usePublicServerInfo();

  const { activeProperty } = useActiveProperty();
  const { myProperties } = usePropertyInfo();
  const { user } = useUserInfo();
  const storage = getStorage();

  const [dataBundle, setDataBundle] = useState<GroupsHomeDataBundle>({
    groupBookings: [],
    simpleGroupBookings: [],
    simpleSailings: [],
    venueTemplates: [],
    allUsers: [],
    sailingDayMap: {},
    groupResources: [],
    groupResourcesTypes: [],
  });

  const mixedDataBundle = useMemo(() => {
    const simpleSailings = dataBundle.simpleSailings.concat(
      dataBundle.auxData?.simpleSailings ?? []
    );

    const sailingDayMap = groupBy(
      simpleSailings
        .map(s =>
          s.days.map((d, i) => ({ dateIso: dateToIsoUTC(d.date), dayIndex: i, sailing: s, day: d }))
        )
        .flat(),
      'dateIso'
    );

    return {
      groupBookings: dataBundle.groupBookings.concat(dataBundle.auxData?.groupBookings ?? []),
      simpleGroupBookings: dataBundle.simpleGroupBookings.concat(
        dataBundle.auxData?.simpleGroupBookings ?? []
      ),
      simpleSailings,
      venueTemplates: dataBundle.venueTemplates.concat(dataBundle.auxData?.venueTemplates ?? []),
      allUsers: dataBundle.allUsers.concat(dataBundle.auxData?.allUsers ?? []),
      sailingDayMap,
    };
  }, [dataBundle]);

  const propertyOpts = myProperties
    .filter(s => s.active)
    .map(s => ({
      key: s._id,
      item: s,
    }));

  const voyageOpts = mixedDataBundle.simpleSailings
    .map(s => s.name)
    .filter(onlyUnique)
    .map(n => ({
      key: n,
      item: n,
    }));

  let allLocations: string[] = [];
  mixedDataBundle.simpleSailings.forEach(sail => {
    sail.days.forEach(day =>
      getDayLocation(day)
        .split('/')
        .forEach(n => allLocations.push(n))
    );
  });
  allLocations = allLocations.filter(onlyUnique);
  const locationOpts = allLocations.map(p => ({
    key: p,
    item: p,
  }));

  const venueOpts = mixedDataBundle.venueTemplates.map(n => ({
    key: n._id,
    item: n,
  }));
  const venueOptsMap = mapBy(venueOpts, 'key');

  const requesterOpts = Object.values(
    mapBy(
      mixedDataBundle.groupBookings.flatMap(gbk => gbk.requests.map(req => req.createdBy)),
      '_id'
    )
  ).map(u => ({
    key: u._id,
    item: u,
  }));

  const statusOpts = venueRequestStatuses.map(s => ({
    key: s,
    item: s,
    variant: makeVariantFromStatus(s),
  }));
  const statusOptsMap = mapBy(statusOpts, 'key');

  const DEFAULT_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => !['cancelled', 'draft'].includes(opt.item)),
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: true,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: true,
      location: true,
      day: true,
      venue: true,
      date: true,
      time: true,
      requester: true,
      createdAt: true,
      status: true,
      foodBeverage: false,
      entertainment: false,
      housekeeping: false,
      markers: true,
      actions: true,
    },
    multiPropertyMode: false,
    showArchivedGroups: storage.showArchivedGroups ?? false,
  };

  const FOOD_BEVERAGE_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => ['approved'].includes(opt.item)),
    selectedGroupResourceType: GroupResourceType.FB,
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: false,
    showArchivedGroups: storage.showArchivedGroups ?? false,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: false,
      location: false,
      day: false,
      venue: true,
      date: true,
      time: true,
      requester: false,
      createdAt: false,
      status: false,
      foodBeverage: true,
      markers: true,
      actions: true,
    },
  };

  const ENTERTAINMENT_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => ['approved'].includes(opt.item)),
    selectedGroupResourceType: GroupResourceType.ENT,
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: false,
    showArchivedGroups: storage.showArchivedGroups ?? false,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: false,
      location: false,
      day: false,
      venue: true,
      date: true,
      time: true,
      requester: false,
      createdAt: false,
      status: false,
      entertainment: true,
      markers: true,
      actions: true,
    },
  };

  const HOUSEKEEPING_FILTERS = {
    selectedStatuses: statusOpts
      .map(sopt => {
        const refOpt = statusOptsMap[sopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: sopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined)
      .filter(opt => ['approved'].includes(opt.item)),
    selectedGroupResourceType: GroupResourceType.HSK,
    groupId: '',
    showAllOfGroupId: '',
    groupByGroup: false,
    showArchivedGroups: storage.showArchivedGroups ?? false,
    visibleFields: {
      subgroup: false,
      name: true,
      voyage: false,
      location: false,
      day: false,
      venue: true,
      date: true,
      time: true,
      requester: false,
      createdAt: false,
      status: false,
      housekeeping: true,
      markers: true,
      actions: true,
    },
  };

  const hourFormat = getHourFormat(activeProperty);
  const [isBusy, setIsBusy] = useState({ exportReport: false, exportBooking: false });
  const [expandedReqId, setExpandedReqId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<GroupsFilters>({
    startDate: serverDate.iso,
    endDate: addDaysToIsoDate(serverDate.iso, 180),
    selectedVoyages: [],
    selectedLocations: [],
    selectedVenues: [],
    selectedProperties: [],
    selectedRequesters: [],
    selectedGroupResourceType: '',
    ...DEFAULT_FILTERS,
  });
  const [selectedTableItems, setSelectedTableItems] = useState<GroupsTableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Exclude<GroupsMode, GroupHistoryMode>>({ type: 'default' });
  const [createSubgroupForm, setCreateSubgroupForm] =
    useState<CreateSubgroupForm | undefined>(undefined);
  const [firstFiltersSet, setFirstFiltersSet] = useState(false);
  const [filteredItems, setFilteredItems] = useState<GroupsTableItem[]>([]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [activeProperty, filters.selectedProperties, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (filters.multiPropertyMode && filters.selectedProperties.length === 0) {
      // user is activating multi property mode, do initializations required
      setFilters({
        ...filters,
        selectedProperties: propertyOpts.map(p => ({
          ...p,
          key: uuid(),
          refKey: p.key,
        })),
      });
    } else if (!filters.multiPropertyMode && filters.selectedProperties.length > 0) {
      // user is deactivating multi property mode, do cleanups required
      setFilters({
        ...filters,
        selectedProperties: [],
      });
    }
  }, [filters.multiPropertyMode]);

  useEffect(() => {
    if (mixedDataBundle.venueTemplates.length && !firstFiltersSet) {
      setFilters({
        ...filters,
        selectedVenues: venueOpts
          .map(vopt => {
            const refOpt = venueOptsMap[vopt.key];
            return refOpt && (user.groupApprovingVenues || []).includes(vopt.item._id)
              ? {
                  key: uuid(),
                  item: vopt.item,
                  refKey: refOpt.key,
                }
              : undefined;
          })
          .filter(notUndefined),
      });
      setFirstFiltersSet(true);
    }
  }, [mixedDataBundle.venueTemplates]);

  async function fetchData() {
    setLoading(true);
    setSelectedTableItems([]);

    try {
      const data = await fetchDataBundle(activeProperty, filters.startDate, filters.endDate);
      const auxData = filters.multiPropertyMode
        ? await fetchMultiDataBundle(
            filters.selectedProperties.map(p => p.item).filter(p => p._id !== activeProperty._id)
          )
        : undefined;
      const sailingDayMap = groupBy(
        data.simpleSailings
          .map(s =>
            s.days.map((d, i) => ({
              dateIso: dateToIsoUTC(d.date),
              dayIndex: i,
              sailing: s,
              day: d,
            }))
          )
          .flat(),
        'dateIso'
      );

      setDataBundle({ ...data, sailingDayMap, auxData });
      return data;
    } finally {
      setLoading(false);
    }
  }

  const fuse = useMemo(() => {
    return new Fuse(
      mixedDataBundle.groupBookings
        .map(gbk =>
          gbk.requests.map(req => ({ _id: req._id, name: req.name, groupName: gbk.party.name }))
        )
        .flat(),
      {
        ...DEFAULT_FUSE_SETTINGS,
        keys: ['name', 'groupName'],
      }
    );
  }, [mixedDataBundle.groupBookings]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  // using fuse to search group IDS was giving weird results
  // plain substring search instead
  const groupIdSearchResultIds = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return undefined;

    return mixedDataBundle.groupBookings
      .filter(gbk => (gbk.party.groupId || '').toLowerCase().includes(term))
      .map(gbk => gbk.requests.map(req => req._id))
      .flat();
  }, [mixedDataBundle.groupBookings, search]);

  const tableItems: GroupsTableItem[] = useMemo(() => {
    const bookingsSortedByGroups = mixedDataBundle.groupBookings
      .filter(gbk => {
        // first filter: only specific groups (by group id)
        const meetsGroupIdSearch = !filters.groupId
          ? true
          : (gbk.party.groupId || '').startsWith(filters.groupId);

        const meetsArchivedGroup = filters.showArchivedGroups || !gbk.archived;
        return meetsGroupIdSearch && meetsArchivedGroup;
      })
      .map(gbk => {
        const relevantReqs = gbk.requests
          // calculate additional filters and add as metadata
          .map(req => {
            const sailingDayInfo = (
              mixedDataBundle.sailingDayMap[dateToIsoUTC(req.date)] || []
            ).find(m => m.dayIndex === req.dayIndex);

            const meetsStartDate =
              dateToIsoUTC(req.date) >= (filters.startDate || dateToIsoUTC(req.date));
            const meetsEndDate =
              dateToIsoUTC(req.date) <= (filters.endDate || dateToIsoUTC(req.date));
            const meetsNameSearch =
              !search ||
              Boolean(fuseSearchResults?.some(sr => sr._id === req._id)) ||
              Boolean(groupIdSearchResultIds?.includes(req._id));
            const meetsVoyageSearch =
              filters.selectedVoyages.length < 1 ||
              filters.selectedVoyages
                .map(sv => sv.item)
                .includes(sailingDayInfo?.sailing.name || '');
            const meetsLocationSearch =
              filters.selectedLocations.length < 1 ||
              intersection(
                (sailingDayInfo?.day.locations ?? []).map(s => s.name.trim().toLowerCase()),
                filters.selectedLocations.map(c => c.item.trim().toLowerCase())
              ).length > 0;
            const meetsVenueSearch =
              filters.selectedVenues.length < 1 ||
              filters.selectedVenues.map(sv => sv.item._id).includes(req.venue._id);
            const meetsStatusSearch =
              filters.selectedStatuses.length < 1 ||
              filters.selectedStatuses.map(sv => sv.item).includes(req.status);
            const meetsRequesterSearch =
              filters.selectedRequesters.length < 1 ||
              filters.selectedRequesters.map(sr => sr.item._id).includes(req.createdBy._id);

            const meetsGroupResourceType =
              !filters.selectedGroupResourceType ||
              req.groupResourceItems
                .map(r => r.resourceType)
                .includes(filters.selectedGroupResourceType) ||
              (filters.selectedGroupResourceType === GroupResourceType.FB &&
                req.resources.length > 0) ||
              (filters.selectedGroupResourceType === GroupResourceType.ENT &&
                req.packages.length > 0) ||
              (filters.selectedGroupResourceType === GroupResourceType.HSK && !!req.dining);

            return {
              ...req,
              _meetsFilters:
                meetsStartDate &&
                meetsEndDate &&
                meetsNameSearch &&
                meetsVoyageSearch &&
                meetsLocationSearch &&
                meetsVenueSearch &&
                meetsStatusSearch &&
                meetsRequesterSearch &&
                meetsGroupResourceType,
            };
          })
          // apply additional filters (and bypass if this group is to be bypassed)
          .filter(req => filters.showAllOfGroupId === gbk._id || req._meetsFilters)
          // sort within group: apply sort by subgroup first, then by date, then by start time
          .sort(
            (a, b) =>
              (a.subgroup?._id || '').localeCompare(b.subgroup?._id || '') ||
              a.date.getTime() - b.date.getTime() ||
              timeCompare(a.startTime, b.startTime)
          );

        // the amount of reqs that did not meet the additional filters
        const amtSkippedReqs = gbk.requests.length - relevantReqs.length;

        // use the metadata of what met filters to find the first 'real' request
        // this is important because this is how we sort the groups in the groups block
        // table. we cannot use the first request that exists (it could be from years ago).
        // we instead use the first request that meets the filters. thankfully we have this
        // info in the metadata calculated before the filters took place.
        const firstReq = relevantReqs.find(req => req._meetsFilters);

        return !firstReq
          ? undefined
          : {
              amtSkippedReqs,
              relevantReqs,
              firstDate: firstReq.date,
              _group: gbk,
            };
      })
      .filter(notUndefined)
      // groups are sorted within themselves, but not yet sorted against other groups
      .sort((a, b) => a.firstDate.getTime() - b.firstDate.getTime())
      .map(gbkBundle => {
        // add additional information (like sailing info) to the bookings
        const { relevantReqs, amtSkippedReqs, _group } = gbkBundle;
        const expandedReqIdx = relevantReqs.findIndex(bkg => bkg._id === expandedReqId);
        const color = hashStringToColor(_group.party.name);

        const rows = relevantReqs.map((req, i) => {
          const sailingDayInfo = (mixedDataBundle.sailingDayMap[dateToIsoUTC(req.date)] || []).find(
            m => m.dayIndex === req.dayIndex
          );

          return {
            id: req._id,
            name: req.name,
            property: _group.property.name,
            venue: req.venue.name,
            voyage: sailingDayInfo?.sailing.name ?? '',
            location: sailingDayInfo?.day ? getDayLocation(sailingDayInfo.day) : '',
            day: req.dayIndex + 1,
            color,
            date: getLocaleDateStringUTC(req.date),
            time: `<span class="whitespace-nowrap">${timeToString(
              req.startTime,
              hourFormat,
              DEFAULT_TIMESTR_FORMAT
            )} -</span>
              ${timeToString(
                calculateTimeOffset(req.startTime, req.duration).time,
                hourFormat,
                DEFAULT_TIMESTR_FORMAT
              )}`,
            requester: makeFullName(req.createdBy.firstName, req.createdBy.lastName),
            createdAt: req.createdAt ? getLocaleDateTimeStringUTC(req.createdAt) : '',
            foodBeverage: req.groupResourceItems
              .filter(r => r.resourceType === GroupResourceType.FB)
              .map(r => `${r.name} (amt. ${r.amount})`),
            entertainment: req.groupResourceItems
              .filter(r => r.resourceType === GroupResourceType.ENT)
              .map(r => `${r.name} (amt. ${r.amount})`),
            housekeeping: req.groupResourceItems
              .filter(r => r.resourceType === GroupResourceType.HSK)
              .map(r => `${r.name} (amt. ${r.amount})`),
            otherResources: formatOtherResources(
              req.groupResourceItems.filter(r => !AllResourceTypes.includes(r.resourceType))
            ),
            status: req.status,
            isFirstReq: !filters.groupByGroup ? false : i === 0,
            isLastReq: !filters.groupByGroup ? false : i === relevantReqs.length - 1,
            isExpandedReq: i === expandedReqIdx,
            totalNumRows: relevantReqs.length,
            hasExpandedReq: expandedReqIdx > -1,
            amtSkippedReqs,
            checked: selectedTableItems.findIndex(st => st.id === req._id) >= 0,
            legacyResources: req.resources,
            _group,
            _sailingDay: sailingDayInfo?.day,
            _booking: req,
          };
        });

        return rows;
      })
      .flat();

    const bookings = bookingsSortedByGroups;
    setFilteredItems(bookings.filter(bk => bk._booking._meetsFilters));

    if (!filters.groupByGroup) {
      // kind of sucks, but now I have to sort again if the user does not want it grouped by group
      bookings.sort(
        (a, b) =>
          a._booking.date.getTime() - b._booking.date.getTime() ||
          timeToString(a._booking.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT).localeCompare(
            timeToString(b._booking.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT)
          )
      );
    }

    return bookings;
  }, [
    filters.selectedVoyages,
    filters.selectedLocations,
    filters.selectedStatuses,
    filters.selectedVenues,
    filters.selectedRequesters,
    filters.groupId,
    filters.showAllOfGroupId,
    filters.groupByGroup,
    filters.showArchivedGroups,
    filters.selectedGroupResourceType,
    mixedDataBundle,
    fuseSearchResults,
    groupIdSearchResultIds,
    expandedReqId,
    selectedTableItems,
  ]);

  function makeDefaultTemplateTdProps(
    data: GroupsTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { isLastReq, isExpandedReq, color } = data;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isLastReq && !isExpandedReq ? 'border-b-2' : undefined,
        isExpandedReq ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
      style: {
        borderBottomColor: isLastReq ? color : '',
      },
    };
  }

  const tableFields: GroupsTableFields[] = [
    {
      key: 'group',
      _visible: filters.groupByGroup,
      behavior: BubbleTableFieldBehavior.MinWidth,
      tdProps: {
        className: 'pr-3-i',
      },
    },
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      _visible: canBulkSelectBookings(user),
    },
    {
      key: 'groupBasic',
      label: 'Group',
      behavior: BubbleTableFieldBehavior.Collapse,
      _visible: !filters.groupByGroup,
    },
    {
      key: 'property',
      label: 'Property',
      _visible: filters.multiPropertyMode,
    },
    {
      key: 'subgroup',
      label: 'Sub',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'name',
      _toggleable: true,
    },
    {
      key: 'voyage',
      _toggleable: true,
    },
    {
      key: 'location',
      _toggleable: true,
    },
    {
      key: 'day',
      _toggleable: true,
    },
    {
      key: 'venue',
      _toggleable: true,
    },
    {
      key: 'markers',
      _toggleable: true,
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'date',
      _toggleable: true,
      behavior: BubbleTableFieldBehavior.MinWidth,
    },
    {
      key: 'time',
      _toggleable: true,
      behavior: BubbleTableFieldBehavior.MinWidth,
    },
    {
      key: 'requester',
      _toggleable: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      _toggleable: true,
      behavior: BubbleTableFieldBehavior.MinWidth,
    },
    {
      key: 'status',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'foodBeverage',
      label: 'Food & Beverage',
      _toggleable: true,
    },
    {
      key: 'entertainment',
      _toggleable: true,
    },
    {
      key: 'housekeeping',
      _toggleable: true,
    },
    {
      key: 'otherResources',
      _toggleable: true,
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<GroupsTableItem>> = [
    {
      key: 'group',
      render: ({ isFirstReq, amtSkippedReqs, color, _group }) =>
        isFirstReq ? (
          <div className="flex flex-col gap-3">
            <div>
              {_group.party.name.split(' ').map((v, i) => (
                <div className="w-32 truncate" key={i}>
                  {v}
                </div>
              ))}
              <div className="w-32 truncate">{_group.party.groupId}</div>
            </div>
            <div className="dropdown-inline-fix">
              <BubbleNestedDropdown
                items={menuDropdownItems}
                variant="white"
                icon="bars"
                style={{ color }}
                className="shadow py-1-i"
                onItemClick={action => handleMenuDropdownAction(action, _group)}
              />
            </div>
            {amtSkippedReqs > 0 && (
              <BubbleButton
                variant="white"
                inverse
                block
                className="block text-xxs text-white text-left font-bold px-1-i py-1-i"
                onClick={() => setFilters({ ...filters, showAllOfGroupId: _group._id })}
              >
                <span>({amtSkippedReqs} reqs hidden)</span>
              </BubbleButton>
            )}
          </div>
        ) : null,
      tdProps: ({ isFirstReq, totalNumRows, hasExpandedReq, color }, fieldProps) => {
        const classes = ['border-b-2 align-top text-md font-semibold text-white relative'];
        if (fieldProps.className) classes.push(fieldProps.className);

        return isFirstReq
          ? {
              rowSpan: totalNumRows + (hasExpandedReq ? 1 : 0),
              className: classes.join(' '),
              style: {
                backgroundColor: color,
                borderColor: color,
              },
            }
          : { style: { display: 'none' } };
      },
    },
    {
      key: 'groupBasic',
      tdProps: makeDefaultTemplateTdProps,
      render: ({ _group, color }) => (
        <div className="dropdown-inline-fix">
          <BubbleNestedDropdown
            items={menuDropdownItems}
            variant="navy"
            style={{ color }}
            className="text-left"
            title={_group.party.name}
            onItemClick={action => handleMenuDropdownAction(action, _group)}
          />
        </div>
      ),
    },
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          checked={item.checked}
          inputClassName="stretched-link bg-white"
          onValue={(checked: boolean) => {
            if (checked) {
              setSelectedTableItems([
                ...selectedTableItems.filter(ti => ti._group._id === item._group._id),
                item,
              ]);
            } else setSelectedTableItems(selectedTableItems.filter(st => st.id !== item.id));
          }}
        />
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'property',
      render: ({ property }) => (
        <BubbleBadge color={hashStringToColor(property)}>{property}</BubbleBadge>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'subgroup',
      render: ({ _group, _booking }) =>
        _booking.subgroup ? (
          <Tooltip text={_booking.subgroup.name || 'No name'}>
            <BubbleBadge
              className="cursor-pointer"
              clickable={canCreateOrEditBookings(user)}
              color={hashStringToColor(_booking.subgroup.label)}
              onClick={() =>
                canCreateOrEditBookings(user) &&
                _booking.subgroup &&
                setMode({
                  type: 'subgroup-edit',
                  groupId: _group._id,
                  subgroupId: _booking.subgroup._id,
                })
              }
            >
              {_booking.subgroup?.label}
            </BubbleBadge>
          </Tooltip>
        ) : (
          <BubbleBadge variant="smoke">N/A</BubbleBadge>
        ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'name',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'voyage',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'location',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'day',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'venue',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'date',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'time',
      render: data => <div dangerouslySetInnerHTML={{ __html: data.time }}></div>,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'requester',
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'createdAt',
      render: data => {
        if (!data.createdAt) return <span className="text-slate italic">N/A</span>;

        const [date, time] = data.createdAt.split(', ');
        return (
          <>
            <div className="block whitespace-nowrap">{date}</div>
            <div className="block whitespace-nowrap">{time}</div>
          </>
        );
      },
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'status',
      render: data => makeBadgeFromStatus(data.status),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'foodBeverage',
      render: data => (
        <div className="flex flex-col gap-2">
          {data.foodBeverage.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate">Resource Library</div>
              <div className="flex flex-wrap">
                {data.foodBeverage.map((fb, i) => (
                  <div key={i} className="whitespace-nowrap">
                    {fb}
                  </div>
                ))}
              </div>
            </div>
          )}
          {data._booking.resources.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate">Legacy</div>
              <div className="flex flex-wrap">
                {data._booking.resources.map((r, i) => (
                  <div key={i} className="whitespace-nowrap">
                    {`${r.name} (amt. ${r.amount})`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'entertainment',
      render: data => (
        <div className="flex flex-col gap-2">
          {data.entertainment.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate">Resource Library</div>
              <div className="flex flex-wrap">
                {data.entertainment.map((ent, i) => (
                  <div key={i} className="whitespace-nowrap">
                    {ent}
                  </div>
                ))}
              </div>
            </div>
          )}
          {data._booking.packages.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate">Legacy</div>
              <div className="flex flex-wrap">
                {data._booking.packages.map((r, i) => (
                  <div key={i} className="whitespace-nowrap">
                    {r}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'housekeeping',
      render: data => (
        <div className="flex flex-col gap-2">
          {data.housekeeping.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate">Resource Library</div>
              <div className="flex flex-wrap">
                {data.housekeeping.map((hsk, i) => (
                  <div key={i} className="whitespace-nowrap">
                    {hsk}
                  </div>
                ))}
              </div>
            </div>
          )}
          {data._booking?.dining && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate">Legacy</div>
              <div className="flex flex-wrap">{data._booking.dining}</div>
            </div>
          )}
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'otherResources',
      render: data => (
        <div className="flex flex-col">
          {data.otherResources.map(({ type, items }) => (
            <div key={type}>
              <span className="font-semibold">{type}:</span>{' '}
              {items.map(r => `${r.name} (amt. ${r.amount})`).join(', ')}
            </div>
          ))}
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'markers',
      render: data => <DayMarkersButton sailingDay={data._sailingDay} />,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'actions',
      render: data => (
        <>
          <BubbleButton
            onClick={() =>
              setExpandedReqId(expandedReqId === data._booking._id ? undefined : data._booking._id)
            }
            className="p-2-i"
            variant="navy"
            inverse
            icon={
              expandedReqId === data._booking._id
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
          />
          {canCreateOrEditBookings(user) && (
            <>
              <BubbleButton
                onClick={() => handleEditBooking(data._group, data._booking)}
                className="p-2-i"
                variant="navy"
                inverse
                disabled={isMultiPropertyUnsafe(data._group.property._id)}
                icon="edit"
              />
              <BubbleButton
                onClick={() => handleCloneBooking(data._group, data._booking)}
                className="p-2-i"
                variant="slate"
                inverse
                disabled={isMultiPropertyUnsafe(data._group.property._id)}
                icon="clone"
              />
            </>
          )}
        </>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
  ];
  const menuDropdownItems: NestedDropdownItem<GroupBooking>[] = [
    canCreateOrEditBookings(user)
      ? {
          key: 'booking-add',
          element: 'Add Group Booking',
        }
      : undefined,
    canCreateOrEditBookings(user)
      ? {
          key: 'edit-party',
          element: 'Edit Group',
        }
      : undefined,
    {
      key: 'memo-csv',
      element: 'Export Memo',
    },
    canExportGroupLetters(user, serverInfo)
      ? {
          key: 'letters',
          element: 'Download Letters',
          submenuItems: [
            {
              key: 'thank-you',
              element: 'Download Thank You Letter',
            },
            {
              key: 'welcome',
              element: 'Download Welcome Letter',
            },
            {
              key: 'prelim',
              element: 'Download Preliminary Letter',
            },
            {
              key: 'final',
              element: 'Download Final Letter',
            },
          ].filter(notUndefined),
        }
      : undefined,
  ].filter(notUndefined);

  function isMultiPropertyUnsafe(propertyId: string) {
    return filters.multiPropertyMode && activeProperty._id != propertyId;
  }

  async function handleMenuDropdownAction(action: string, group: GroupBooking) {
    const getExportOptions = async (
      confirmButton: string,
      showApprovedOnly: boolean = true
    ): Promise<ExportOptions | null> => {
      const opts: ExportOptions = { approvedOnly: false };

      const shouldContinue = await Alert.question({
        htmlContent: (
          <div className="p-3 w-full">
            <div className="mb-1 text-navy uppercase font-semibold text-xs">
              Start Date (Optional)
            </div>
            <BubbleInput
              type="date"
              className="mb-3"
              block
              value={opts.fromDate}
              onValue={v => (opts.fromDate = v)}
            />
            <div className="mb-1 text-navy uppercase font-semibold text-xs">
              End Date (Optional)
            </div>
            <BubbleInput
              type="date"
              block
              className="mb-3"
              value={opts.toDate}
              onValue={v => (opts.toDate = v)}
            />
            {showApprovedOnly && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="bubble-input"
                  onChange={evt => (opts.approvedOnly = evt.target.checked)}
                />
                <span className="ml-2 text-sm">Approved Only</span>
              </div>
            )}
          </div>
        ),
        title: 'Select Date Range',
        yesButton: confirmButton,
        noButton: 'Cancel',
      });

      return shouldContinue ? opts : null;
    };

    switch (action) {
      case 'booking-add':
        if (isMultiPropertyUnsafe(group.property._id)) return;
        handleAddBookingToGroup(group);
        break;
      case 'edit-party':
        if (isMultiPropertyUnsafe(group.property._id)) return;
        setMode({ type: 'group-edit', groupId: group._id });
        break;
      case 'memo-csv':
        const memoOpts = await getExportOptions('Export Memo');
        if (!memoOpts) return;

        const result = await exportGroupMemo(
          group._id,
          memoOpts.approvedOnly,
          memoOpts.fromDate,
          memoOpts.toDate
        );

        return Alert.success({
          htmlContent: (
            <div className="pt-2 pb-4">
              <Anchor target="_blank" href={result}>
                Download PDF
              </Anchor>
            </div>
          ),
        });
      case 'thank-you':
        const thankYouOpts = await getExportOptions('Export Thank You Letter', false);
        if (!thankYouOpts) return;
        await handleExportLetters('thank-you', group, thankYouOpts);
        break;
      case 'welcome':
        const welcomeOpts = await getExportOptions('Export Welcome Letter', false);
        if (!welcomeOpts) return;
        await handleExportLetters('welcome', group, welcomeOpts);
        break;
      case 'prelim':
        const prelimOpts = await getExportOptions('Export Preliminary Letter', false);
        if (!prelimOpts) return;
        await handleExportLetters('prelim', group, prelimOpts);
        break;
      case 'final':
        const finalOpts = await getExportOptions('Export Final Letter', false);
        if (!finalOpts) return;
        await handleExportLetters('final', group, finalOpts);
        break;
    }
  }

  const statusDropdownItems = [
    { key: 'bulk-approve', element: 'Approved' },
    { key: 'bulk-reject', element: 'Rejected' },
    { key: 'bulk-draft', element: 'Draft' },
    { key: 'bulk-modified', element: 'Modified' },
    { key: 'bulk-pending', element: 'Pending' },
    { key: 'bulk-cancel', element: 'Cancelled' },
  ];

  async function handleStatusDropdownAction(action: DropdownItem) {
    switch (action.key) {
      case 'bulk-approve':
        await handleBulkStatusChangeBookings('approved');
        break;
      case 'bulk-reject':
        await handleBulkStatusChangeBookings('rejected');
        break;
      case 'bulk-draft':
        await handleBulkStatusChangeBookings('draft');
        break;
      case 'bulk-modified':
        await handleBulkStatusChangeBookings('modified');
        break;
      case 'bulk-pending':
        await handleBulkStatusChangeBookings('pending');
        break;
      case 'bulk-cancel':
        await handleBulkStatusChangeBookings('cancelled');
        break;
    }
  }

  function tableDetailsRender(item: GroupsTableItem) {
    return (
      <GroupsGlance
        user={user}
        property={item._group.property}
        groupId={item._group._id}
        bookingId={item._booking._id}
        data={{
          group: bare(item._group),
          booking: item._booking,
          sailingBundle: findSailingBundleByDateAndDay(
            item._booking.date,
            item._booking.dayIndex,
            mixedDataBundle.simpleSailings
          ),
        }}
        onFetchData={fetchData}
      />
    );
  }

  function handleCloseGroupBookingForm() {
    setMode({ type: 'default' });
  }

  function handleAddBookingToGroup(group: GroupBooking) {
    setMode({ type: 'create', groupId: group._id, startAtPage: 1 });
  }

  function handleEditBooking(group: GroupBooking, booking: VenueRequest) {
    setMode({ type: 'edit', groupId: group._id, bookingId: booking._id });
  }

  function handleCloneBooking(group: GroupBooking, booking: VenueRequest) {
    setMode({ type: 'create', groupId: group._id, bookingId: booking._id, startAtPage: 0 });
  }

  async function handleExportGroupBookings() {
    setIsBusy(prev => ({
      ...prev,
      exportBooking: true,
    }));
    try {
      await exportGroupBookings(activeProperty._id);
    } finally {
      setIsBusy(prev => ({
        ...prev,
        exportBooking: false,
      }));
    }
  }

  async function handleExportLetters(
    letterName: string,
    group: GroupBooking,
    exportOpts: ExportOptions
  ) {
    const { fromDate, toDate } = exportOpts;
    const response = await exportLetter(
      activeProperty._id,
      group._id,
      letterName,
      true,
      fromDate,
      toDate
    );
    const blob = new Blob([new Uint8Array(response.data)], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    downloadBlob(blob, `export_${letterName}_letter_${randomString(12)}.docx`);
  }

  async function askReportOptions(): Promise<{
    approvedOnly: boolean;
    includeBalance: boolean;
  } | null> {
    let approvedOnly = false;
    let includeBalance = false;
    const confirmed = await Alert.question({
      htmlContent: (
        <div className="p-3 w-full flex flex-col gap-2">
          <div className="flex relative justify-center">
            <input
              type="checkbox"
              className="bubble-input"
              onChange={e => {
                approvedOnly = e.target.checked;
              }}
            />
            <span className="ml-1">Approved Only</span>
          </div>
          <div className="flex relative justify-center">
            <input
              type="checkbox"
              className="bubble-input"
              onChange={e => {
                includeBalance = e.target.checked;
              }}
            />
            <span className="ml-1">Include Payment Status & Balance</span>
          </div>
        </div>
      ),
      title: 'Status',
      yesButton: 'Ok',
      noButton: 'Cancel',
    });
    if (!confirmed) return null;
    return { approvedOnly, includeBalance };
  }

  async function handleExportReport() {
    setIsBusy(prev => ({ ...prev, exportReport: true }));

    const { startDate, endDate } = filters;
    const requests = filteredItems
      ?.filter(gbk => gbk._booking)
      .map(item => ({
        voyage: item.voyage,
        bookingId: item._group._id,
        requestId: item._booking._id,
      }));
    const options = await askReportOptions();
    if (!options) {
      setIsBusy(prev => ({ ...prev, exportReport: false }));
      return;
    }
    const { approvedOnly, includeBalance } = options;

    const url = await safeFetch(
      groupBookingClient.generateReport({
        body: requests ?? [],
        query: {
          approvedOnly: approvedOnly.toString(),
          from: startDate || '',
          to: endDate || '',
          balance: includeBalance.toString(),
        },
        params: { propertyId: activeProperty._id },
        headers: {
          authorization: `Bearer ${getUserToken()}`,
        },
      }),
      'string'
    );

    setIsBusy(prev => ({ ...prev, exportReport: false }));

    return Alert.success({
      htmlContent: (
        <div className="pt-2 pb-4">
          <Anchor target="_blank" href={url}>
            Download PDF
          </Anchor>
        </div>
      ),
    });
  }

  async function handleExportReportCSV() {
    setIsBusy(prev => ({ ...prev, exportReport: true }));

    const requests = filteredItems?.map(item => ({
      voyage: item.voyage,
      bookingId: item._group._id,
      requestId: item._booking._id,
    }));

    const options = await askReportOptions();
    if (!options) {
      setIsBusy(prev => ({ ...prev, exportReport: false }));
      return;
    }
    const { approvedOnly, includeBalance } = options;

    try {
      await exportGroupReportCSV(activeProperty._id, requests ?? [], approvedOnly, includeBalance);
    } finally {
      setIsBusy(prev => ({ ...prev, exportReport: false }));
    }
  }

  async function handleExportGroupBookingsBrand() {
    setIsBusy(prev => ({
      ...prev,
      exportBooking: true,
    }));

    try {
      await exportGroupBookingsBrand();
    } finally {
      setIsBusy(prev => ({
        ...prev,
        exportBooking: false,
      }));
    }
  }

  async function handleCreateSubgroup() {
    if (!createSubgroupForm || selectedTableItems.length < 1) return;

    const label = `${createSubgroupForm.letter}${createSubgroupForm.number}`;
    if (label.length !== 2) return;

    const { _group } = selectedTableItems[0];
    const bareGroup = bare(_group);

    let existingSubgroup = bareGroup.subgroups.find(sg => sg.label === label);
    if (!existingSubgroup) {
      existingSubgroup = {
        _id: new ObjectId().toHexString(),
        label,
        name: '',
        notes: '',
        groupGuideName: '',
        groupGuideCabin: '',
        attendees: 0,
        files: [],
      };
      bareGroup.subgroups.push(existingSubgroup);
    }

    const relevantReqs = selectedTableItems.map(sti => sti._booking._id);
    bareGroup.requests.forEach(req => {
      if (relevantReqs.includes(req._id)) {
        req.subgroup = existingSubgroup?._id;
      }
    });

    await updateGroupBooking(bareGroup);
    await fetchData();

    setCreateSubgroupForm(undefined);
    setMode({ type: 'subgroup-edit', groupId: _group._id, subgroupId: existingSubgroup._id });
  }

  async function handleClearSubgroup() {
    if (!createSubgroupForm || selectedTableItems.length < 1) return;

    const { _group } = selectedTableItems[0];
    const bareGroup = bare(_group);

    const relevantReqs = selectedTableItems.map(sti => sti._booking._id);
    bareGroup.requests.forEach(req => {
      if (relevantReqs.includes(req._id)) {
        req.subgroup = undefined;
      }
    });

    await updateGroupBooking(bareGroup);
    await fetchData();

    setCreateSubgroupForm(undefined);
  }

  function bookingMeetsBookingHours(booking: VenueRequest) {
    return venueRequestMeetsBookingHours(
      booking,
      findSailingBundleByDateAndDay(booking.date, booking.dayIndex, mixedDataBundle.simpleSailings),
      timeToMinutes(activeProperty.dayStart || DEFAULT_DAY_START)
    );
  }

  async function handleBulkStatusChangeBookings(status: VenueRequestStatus) {
    const itemsToProcess = selectedTableItems
      .filter(ti => ti._booking.status !== status)
      .filter(ti =>
        canChangeBookingStatus(status, user, ti._booking, bookingMeetsBookingHours(ti._booking))
      );

    const shouldContinue = await Alert.question({
      message: `This action will change the status of the booking(s) to ${status.toLocaleUpperCase()}, provided that you have the necessary permissions to do so. Are you certain that you want to proceed with this change?`,
      title: `Change Group Booking Status`,
      yesButton: `Yes, change group booking status`,
      noButton: 'No, cancel',
      type: 'yesno',
    });

    if (!shouldContinue) return;

    await bulkBookingStatusChange(
      itemsToProcess.map(itp => ({
        group: bare(itp._group),
        booking: bareVenueRequest(itp._booking),
      })),
      user,
      status
    );

    await fetchData();
  }

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : filters.visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  const filterPresetOpts = [
    { key: 'default', element: 'Default Filters' },
    { key: 'foodBeverage', element: 'Food & Beverage Focus' },
    { key: 'entertainment', element: 'Entertainment Focus' },
    { key: 'housekeeping', element: 'Housekeeping Focus' },
  ];

  function handleChangeFilterPresetOpts(item: DropdownItem) {
    switch (item.key) {
      case 'default':
        setFilters({ ...filters, ...DEFAULT_FILTERS });
        break;
      case 'foodBeverage':
        setFilters({ ...filters, ...FOOD_BEVERAGE_FILTERS });
        break;
      case 'entertainment':
        setFilters({ ...filters, ...ENTERTAINMENT_FILTERS });
        break;
      case 'housekeeping':
        setFilters({ ...filters, ...HOUSEKEEPING_FILTERS });
        break;
    }
  }

  function handleClearAllFilters() {
    setFilters({
      ...filters,
      startDate: dateToIsoUTC(),
      endDate: dateToIsoUTC(addDaysToDateUTC(new Date(dateToIsoUTC()), 180)),
      selectedVoyages: [],
      selectedLocations: [],
      selectedVenues: [],
      selectedRequesters: [],
      selectedStatuses: [],
      groupId: '',
      showAllOfGroupId: '',
    });
  }

  return (
    <PageWrapper {...props}>
      <Page className="Groups absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-forest-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-3 bg-forest-darker`}>
            <div className="grid gap-2">
              {canCreateOrEditBookings(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  block
                  disabled={loading}
                  onClick={() => setMode({ type: 'create', startAtPage: 0 })}
                  style={{ fontSize: '12px' }}
                  icon="sparkles"
                >
                  New Group Booking
                </BubbleButton>
              )}

              <div className="w-full flex items-stretch">
                <BubbleOverlay
                  loading={isBusy.exportBooking}
                  loadingClassName="rounded"
                  className="flex-grow"
                >
                  <BubbleButton
                    variant="slate"
                    className="text-left"
                    block
                    disabled={isBusy.exportBooking}
                    onClick={handleExportGroupBookings}
                    icon="file-export"
                    style={
                      canViewGroupsMultiPropertyCheckbox(serverInfo)
                        ? {
                            paddingRight: 0,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            fontSize: '12px',
                          }
                        : undefined
                    }
                  >
                    Export Group Bookings
                  </BubbleButton>
                </BubbleOverlay>
                {canViewGroupsMultiPropertyCheckbox(serverInfo) && (
                  <div className="dropdown-inline-flex-fix items-stretch">
                    <Dropdown
                      items={[{ key: 'brand-export', element: 'Export all properties' }]}
                      className="flex items-stretch"
                      onSelect={handleExportGroupBookingsBrand}
                    >
                      {({ toggleRef, ...rest }) => (
                        <BubbleButton
                          buttonRef={toggleRef}
                          {...rest}
                          variant="slate"
                          className="shadow py-1-i px-2-i"
                          icon="chevron-down"
                          disabled={isBusy.exportBooking}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                          }}
                        />
                      )}
                    </Dropdown>
                  </div>
                )}
              </div>
              <div className="w-full flex items-stretch">
                <BubbleOverlay
                  loading={isBusy.exportReport}
                  loadingClassName="rounded"
                  className="flex-grow"
                  hidden={filters.multiPropertyMode}
                >
                  <BubbleButton
                    variant="slate"
                    className="text-left"
                    block
                    disabled={loading}
                    onClick={handleExportReport}
                    icon="file-export"
                    style={
                      canViewGroupsMultiPropertyCheckbox(serverInfo)
                        ? {
                            paddingRight: 0,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            fontSize: '12px',
                          }
                        : undefined
                    }
                  >
                    Export Report
                  </BubbleButton>
                </BubbleOverlay>
                <div
                  className="dropdown-inline-flex-fix items-stretch"
                  hidden={filters.multiPropertyMode}
                >
                  <Dropdown
                    items={[{ key: 'brand-export', element: 'Export as CSV' }]}
                    className="flex items-stretch"
                    onSelect={handleExportReportCSV}
                  >
                    {({ toggleRef, ...rest }) => (
                      <BubbleButton
                        buttonRef={toggleRef}
                        {...rest}
                        variant="slate"
                        className="shadow py-1-i px-2-i"
                        icon="chevron-down"
                        disabled={loading}
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        }}
                      />
                    )}
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
          <BubbleAccordion
            variant="forest"
            className="flex-grow flex flex-col border-t border-forest-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4">
              <div className="dropdown-inline-fix sticky top-0 z-10 bg-forest-darkest p-4 -mt-4 grid gap-2">
                <Dropdown items={filterPresetOpts} onSelect={handleChangeFilterPresetOpts}>
                  {({ toggleRef, ...rest }) => (
                    <BubbleButton
                      buttonRef={toggleRef}
                      {...rest}
                      variant="banana"
                      className="text-left"
                      block
                      icon="bolt-lightning"
                    >
                      Filter Presets
                    </BubbleButton>
                  )}
                </Dropdown>
                <BubbleButton
                  variant="smoke"
                  className="text-left"
                  block
                  icon="xmark"
                  onClick={handleClearAllFilters}
                >
                  Clear All Filters
                </BubbleButton>
              </div>
              {canViewGroupsMultiPropertyCheckbox(serverInfo) && (
                <div className="grid gap-1 text-white-i px-4">
                  <BubbleCheckbox
                    label="Multi property"
                    className="text-white"
                    variant="forest"
                    checked={filters.multiPropertyMode}
                    onValue={(checked: boolean) => {
                      setFilters({ ...filters, multiPropertyMode: checked });
                    }}
                  />
                  {filters.multiPropertyMode && (
                    <BubbleTypeahead
                      block
                      variant="forest"
                      placeholder="Filter properties shown..."
                      menuClassName="max-h-80 overflow-y-auto"
                      value={filters.selectedProperties}
                      labelExtractor={p => p.name}
                      options={propertyOpts}
                      onAdd={v =>
                        setFilters({
                          ...filters,
                          selectedProperties: [...filters.selectedProperties, v],
                        })
                      }
                      onRemove={v =>
                        setFilters({
                          ...filters,
                          selectedProperties: filters.selectedProperties.filter(
                            v2 => v2.key !== v.key
                          ),
                        })
                      }
                      onClearAll={() => setFilters({ ...filters, selectedProperties: [] })}
                    />
                  )}
                </div>
              )}
              <BubbleFormGroup label="Dates" labelClassName="text-white-i" className="px-4">
                <div className="grid grid-cols-1 gap-1">
                  <BubbleDebounce
                    onTrigger={[
                      (date: string) => {
                        let newStartDate = date;
                        let newEndDate = filters.endDate;
                        let halfYearAfterStartDate = addDaysToDateUTC(new Date(newStartDate), 180);

                        // when changing the start date, we 'clamp' the end date if its too far
                        // from the start date to a maximum of half a year (180 days)
                        if (
                          !newEndDate ||
                          newStartDate > newEndDate ||
                          new Date(newEndDate) > halfYearAfterStartDate
                        ) {
                          newEndDate = dateToIsoUTC(halfYearAfterStartDate);
                        }

                        setFilters({
                          ...filters,
                          startDate: newStartDate,
                          endDate: newEndDate,
                        });
                      },
                      500,
                    ]}
                  >
                    {({ debounce }) => (
                      <BubbleInput
                        variant="forest"
                        type="date"
                        value={filters.startDate}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                  <BubbleDebounce
                    onTrigger={[(date: string) => setFilters({ ...filters, endDate: date }), 500]}
                  >
                    {({ debounce }) => (
                      <BubbleInput
                        variant="forest"
                        type="date"
                        value={filters.endDate}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                </div>
              </BubbleFormGroup>
              <BubbleFormGroup label="Group" labelClassName="text-white-i" className="px-4">
                <BubbleInput
                  block
                  variant="forest"
                  placeholder="Filter by group ID..."
                  type="text"
                  value={filters.groupId}
                  onValue={val =>
                    setFilters({
                      ...filters,
                      groupId: val,
                    })
                  }
                />
                <BubbleCheckbox
                  label="Group rows by group"
                  className="text-white mt-1"
                  variant="forest"
                  checked={filters.groupByGroup}
                  onValue={(checked: boolean) => {
                    setFilters({ ...filters, groupByGroup: checked });
                  }}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Voyages" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter voyages shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedVoyages}
                  options={voyageOpts}
                  onAdd={v =>
                    setFilters({ ...filters, selectedVoyages: [...filters.selectedVoyages, v] })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedVoyages: filters.selectedVoyages.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedVoyages: [] })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Locations" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter locations shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedLocations}
                  options={locationOpts}
                  onAdd={l =>
                    setFilters({ ...filters, selectedLocations: [...filters.selectedLocations, l] })
                  }
                  onRemove={l =>
                    setFilters({
                      ...filters,
                      selectedLocations: filters.selectedLocations.filter(sl => sl.key !== l.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedLocations: [] })}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Venues" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter venues shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  labelExtractor={opt => opt.name}
                  value={filters.selectedVenues}
                  options={venueOpts}
                  onAdd={v =>
                    setFilters({ ...filters, selectedVenues: [...filters.selectedVenues, v] })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedVenues: filters.selectedVenues.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedVenues: [] })}
                />
              </BubbleFormGroup>

              <BubbleFormGroup label="Requester" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter requesters shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  labelExtractor={opt => makeFullName(opt.firstName, opt.lastName)}
                  value={filters.selectedRequesters}
                  options={requesterOpts}
                  onAdd={v =>
                    setFilters({
                      ...filters,
                      selectedRequesters: [...filters.selectedRequesters, v],
                    })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedRequesters: filters.selectedRequesters.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedRequesters: [] })}
                />
              </BubbleFormGroup>

              <BubbleFormGroup label="Status" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="forest"
                  placeholder="Filter statuses shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedStatuses}
                  options={statusOpts}
                  onAdd={v =>
                    setFilters({ ...filters, selectedStatuses: [...filters.selectedStatuses, v] })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedStatuses: filters.selectedStatuses.filter(v2 => v2.key !== v.key),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedStatuses: [] })}
                />
              </BubbleFormGroup>

              <BubbleFormGroup label="Archived" labelClassName="text-white-i" className="px-4">
                <div className="grid grid-cols-1">
                  <div className="text-white flex relative py-1">
                    <BubbleInput
                      type="checkbox"
                      variant="forest"
                      className="mr-1"
                      inputClassName="stretched-link"
                      checked={filters.showArchivedGroups}
                      onValue={checked => {
                        setStorage('showArchivedGroups', checked);
                        setFilters({ ...filters, showArchivedGroups: checked });
                      }}
                    />
                    <span>Show Archived Groups</span>
                  </div>
                </div>
              </BubbleFormGroup>

              <BubbleFormGroup label="Fields" labelClassName="text-white-i" className="px-4 pb-4">
                <div className="grid grid-cols-1 gap-1">
                  {toggleableTableFields.map(tf => (
                    <BubbleCheckbox
                      key={tf.key}
                      label={tf.label || camelToSentenceCase(tf.key)}
                      className="text-white"
                      variant="forest"
                      checked={filters.visibleFields[tf.key]}
                      onValue={chk =>
                        setFilters({
                          ...filters,
                          visibleFields: { ...filters.visibleFields, [tf.key]: chk },
                        })
                      }
                    />
                  ))}
                </div>
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 bg-blue-lightest"
          >
            <div className="mb-3 w-full flex items-end px-3">
              <GroupsNavigation active="bookings"></GroupsNavigation>
            </div>
          </div>
          <div className="GroupsBody mx-5 content-minus-logo min-w-900">
            <BubbleOverlay loading={loading}>
              <BubbleTable
                responsive
                striped
                headVariant="forest"
                fields={visibleTableFields}
                items={tableItems}
                onRowClick={item => {
                  setExpandedReqId(
                    expandedReqId === item._booking._id ? undefined : item._booking._id
                  );
                }}
                stickyHeader={130}
                templates={tableTemplates}
                detailsKey={expandedReqId}
                detailsRender={tableDetailsRender}
                detailsClassName="bg-bubblegum-lightest"
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div className="px-4 pt-4 space-x-3 pb-1 flex items-center">
                    {selectedTableItems.length > 0 && (
                      <>
                        {selectedTableItems
                          .map(sti =>
                            canApproveOrRejectABooking(
                              user,
                              sti._booking,
                              bookingMeetsBookingHours(sti._booking)
                            )
                          )
                          .findIndex(v => !v) < 0 && (
                          <div className="dropdown-inline-fix">
                            <Dropdown
                              items={statusDropdownItems}
                              onSelect={action => handleStatusDropdownAction(action)}
                            >
                              {({ toggleRef, ...rest }) => (
                                <BubbleButton buttonRef={toggleRef} {...rest} variant="lime">
                                  Change Status
                                </BubbleButton>
                              )}
                            </Dropdown>
                          </div>
                        )}
                        {canCreateOrEditBookings(user) && (
                          <BubbleButton
                            variant="grape"
                            onClick={() =>
                              setCreateSubgroupForm(
                                generateDefaultSubgroupForm(selectedTableItems[0]._group.subgroups)
                              )
                            }
                            disabled={isMultiPropertyUnsafe(
                              selectedTableItems[0]._group.property._id
                            )}
                          >
                            Apply Subgroup
                          </BubbleButton>
                        )}
                        <BubbleButton
                          variant="white"
                          inverse
                          className="m-0-i"
                          onClick={() => setSelectedTableItems([])}
                        >
                          Clear
                        </BubbleButton>
                      </>
                    )}
                    <div className="flex-grow m-0-i">
                      <BubbleInput
                        variant="forest"
                        inputClassName="outline-none-i"
                        type="text"
                        block
                        value={search}
                        onValue={setSearch}
                        placeholder="Search by Group ID, Group Name, or Group Booking Name..."
                      />
                    </div>
                  </div>
                )}
              />
            </BubbleOverlay>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode.type !== 'default' && (
          <BubbleDrawer hideTools containerClassName="bg-eggshell">
            <GroupBookingForm
              mode={mode}
              data={dataBundle}
              property={activeProperty}
              user={user}
              groupTypes={serverInfo.brand.groupTypes}
              onClose={handleCloseGroupBookingForm}
              defaultStartDate={filters.startDate}
              defaultEndDate={filters.endDate}
              onSubmitGroupBookings={fetchData}
            />
          </BubbleDrawer>
        )}
        {createSubgroupForm && (
          <Popup title="Apply Subgroup" onClose={() => setCreateSubgroupForm(undefined)}>
            <div style={{ width: '600px' }}>
              <hr />
              <div className="p-4">
                {createSubgroupForm._advanced && (
                  <div className="mb-5">
                    <BubbleFormGroup label="Letter" asterisk className="mb-5">
                      <div className="grid gap-3 grid-cols-10">
                        {Array.from(Array(26))
                          .map((_, i) => i + 65)
                          .map(x => (
                            <BubbleButton
                              variant={
                                createSubgroupForm.letter === String.fromCharCode(x)
                                  ? 'lime'
                                  : 'smoke'
                              }
                              key={x}
                              onClick={() =>
                                setCreateSubgroupForm({
                                  ...createSubgroupForm,
                                  letter: String.fromCharCode(x),
                                })
                              }
                            >
                              {String.fromCharCode(x)}
                            </BubbleButton>
                          ))}
                      </div>
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Number" asterisk>
                      <div className="grid gap-3 grid-cols-10">
                        {Array.from(Array(10).keys()).map(x => (
                          <BubbleButton
                            variant={createSubgroupForm.number === x.toString() ? 'lime' : 'smoke'}
                            key={x}
                            onClick={() =>
                              setCreateSubgroupForm({
                                ...createSubgroupForm,
                                number: x.toString(),
                              })
                            }
                          >
                            {x}
                          </BubbleButton>
                        ))}
                      </div>
                    </BubbleFormGroup>
                  </div>
                )}
                {createSubgroupForm.letter.length > 0 && createSubgroupForm.number.length > 0 && (
                  <div className="flex items-center">
                    <span className="italic mr-2 text-slate">Will show as:</span>
                    <BubbleBadge
                      variant="smoke"
                      color={hashStringToColor(
                        `${createSubgroupForm.letter}${createSubgroupForm.number}`
                      )}
                    >
                      {createSubgroupForm.letter}
                      {createSubgroupForm.number}
                    </BubbleBadge>
                    {!createSubgroupForm._advanced && (
                      <BubbleButton
                        variant="navy"
                        inverse
                        className="p-1-i ml-auto"
                        onClick={() =>
                          setCreateSubgroupForm({ ...createSubgroupForm, _advanced: true })
                        }
                      >
                        OR, specify a custom label
                      </BubbleButton>
                    )}
                  </div>
                )}
              </div>
              <hr />
              <div className="p-4">
                <div className="flex items-center">
                  <BubbleButton
                    variant="navy"
                    className="mr-3"
                    disabled={
                      createSubgroupForm.letter.length < 1 || createSubgroupForm.number.length < 1
                    }
                    onClick={handleCreateSubgroup}
                  >
                    Apply Subgroup
                  </BubbleButton>
                  <BubbleButton variant="crimson" onClick={handleClearSubgroup}>
                    Remove (Mark as N/A)
                  </BubbleButton>
                </div>
              </div>
            </div>
          </Popup>
        )}
      </Page>
    </PageWrapper>
  );
};

export default GroupsHome;

export function makeVariantFromStatus(status: VenueRequestStatus) {
  let variant: BubbleBadgeProps['variant'] = 'smoke';

  switch (status) {
    case 'approved':
      variant = 'lime';
      break;
    case 'cancelled':
    case 'rejected':
      variant = 'crimson';
      break;
    case 'draft':
    case 'modified':
      variant = 'banana';
      break;
    default:
      variant = 'smoke';
      break;
  }

  return variant;
}

export function makeBadgeFromStatus(status: VenueRequestStatus) {
  const variant = makeVariantFromStatus(status);

  return (
    <BubbleBadge variant={variant} className="mt-1 mr-1">
      {status}
    </BubbleBadge>
  );
}

export function makeTextFromStatus(status: VenueRequestStatus) {
  const variant = makeVariantFromStatus(status);

  return <span className={`text-xs uppercase font-bold text-${variant}`}>{status}</span>;
}
